/**
 * Right Hand DMS woodblock design system.
 *
 * Tokens and base styles live in tokens.css (scoped to `.rhd`), component styles in
 * components.css. Wrap a tree in <RhdRoot> to opt in. The marketing site is built from these
 * parts; app screens move over to them next.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './tokens.css';
import './components.css';
const cx = (...parts)=>parts.filter(Boolean).join(' ');
/* ---------- root ---------- */ /** Opts a subtree into the design system (tokens, paper + grain background, base type). */ export function RhdRoot({ theme, className, children, ...rest }) {
    return /*#__PURE__*/ _jsx("div", {
        className: cx('rhd', className),
        "data-theme": theme,
        ...rest,
        children: children
    });
}
export function Container({ className, ...rest }) {
    return /*#__PURE__*/ _jsx("div", {
        className: cx('rhd-container', className),
        ...rest
    });
}
function text(cls, fallback) {
    return function Text({ as, className, ...rest }) {
        const El = as ?? fallback;
        return /*#__PURE__*/ _jsx(El, {
            className: cx(cls, className),
            ...rest
        });
    };
}
/** Hero display (Mincho 800, up to 68px). */ export const Display = text('rhd-display', 'h1');
/** Page title (Mincho 800, up to 54px). */ export const PageTitle = text('rhd-h1', 'h1');
/** Section heading (Mincho 700, up to 48px). */ export const SectionTitle = text('rhd-h2', 'h2');
/** Sub-section heading (Mincho 700, 28px). */ export const Subhead = text('rhd-h3', 'h3');
/** Intro paragraph under a title. */ export const Lead = text('rhd-lead', 'p');
export const Body = text('rhd-body', 'p');
export const Small = text('rhd-small', 'p');
/** Tracked uppercase label. */ export const Caps = text('rhd-caps', 'span');
/* ---------- stamps ---------- */ /** Vermilion kanji block used for section labels. */ export function Stamp({ children, size = 'md', style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-stamp', size === 'sm' && 'rhd-stamp--sm'),
        style: style,
        children: children
    });
}
/** Kanji stamp + tracked label, the header above every section ("検索 · SEARCH"). */ export function SectionLabel({ k, children, tight, className, style }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-label', tight && 'rhd-label--tight', className),
        style: style,
        children: [
            /*#__PURE__*/ _jsx(Stamp, {
                size: tight ? 'sm' : 'md',
                children: k
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-caps",
                children: children
            })
        ]
    });
}
// Glyph size per seal size, from the design (not a single ratio).
const HANKO_FONT = {
    16: 12,
    32: 21,
    36: 23,
    40: 25,
    44: 28,
    64: 42
};
/** The 右 hanko seal. `size` is the square in px. */ export function Hanko({ size = 36, plain, ring = true, style }) {
    const inset = size >= 60 ? [
        3,
        5.5
    ] : [
        2.5,
        4
    ];
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-hanko', plain && 'rhd-hanko--plain'),
        style: {
            width: size,
            height: size,
            fontSize: HANKO_FONT[size] ?? Math.round(size * 0.64),
            borderRadius: size >= 60 ? 4 : size >= 30 ? 3 : 1,
            boxShadow: ring ? `inset 0 0 0 ${inset[0]}px var(--beni),inset 0 0 0 ${inset[1]}px var(--paper)` : undefined,
            ...style
        },
        children: "右"
    });
}
/** Circled auction grade, e.g. 3.5. */ export function GradeStamp({ children, size = 40, style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-grade",
        style: {
            width: size,
            height: size,
            ...style
        },
        children: children
    });
}
/** Highlighted frame (chassis) number. */ export function FrameNo({ children, style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-frame-no",
        style: style,
        children: children
    });
}
/* ---------- logo ---------- */ /** Hanko + "Right Hand DMS" lockup. */ export function Logo({ href = '/', size = 'nav', onDark }) {
    const hanko = {
        nav: 36,
        footer: 40,
        login: 44
    }[size];
    const word = {
        nav: 19,
        footer: 20,
        login: 22
    }[size];
    return /*#__PURE__*/ _jsxs("a", {
        href: href,
        className: "rhd-logo",
        style: onDark ? {
            color: 'inherit'
        } : undefined,
        children: [
            /*#__PURE__*/ _jsx(Hanko, {
                size: hanko,
                style: onDark ? {
                    color: '#F3EADA',
                    boxShadow: 'inset 0 0 0 2.5px var(--beni),inset 0 0 0 4px #F3EADA'
                } : undefined
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "rhd-logo__word",
                style: {
                    fontSize: word
                },
                children: [
                    "Right Hand",
                    size === 'nav' ? ' ' : ' DMS',
                    size === 'nav' && /*#__PURE__*/ _jsx("span", {
                        className: "rhd-logo__dms",
                        children: "DMS"
                    })
                ]
            })
        ]
    });
}
function buttonClass({ variant = 'primary', size = 'lg', block }, className) {
    return cx('rhd-btn', `rhd-btn--${variant}`, size !== 'lg' && `rhd-btn--${size}`, block && 'rhd-btn--block', className);
}
/** Link styled as a button (all marketing CTAs are links). */ export function ButtonLink({ variant, size, block, className, ...rest }) {
    return /*#__PURE__*/ _jsx("a", {
        className: buttonClass({
            variant,
            size,
            block
        }, className),
        ...rest
    });
}
export function Button({ variant, size, block, className, type = 'button', ...rest }) {
    return /*#__PURE__*/ _jsx("button", {
        type: type,
        className: buttonClass({
            variant,
            size,
            block
        }, className),
        ...rest
    });
}
/* ---------- surfaces ---------- */ /** Card on paper with an ink border; `shadow` adds the offset mis-registration print shadow. */ export function Card({ shadow, className, ...rest }) {
    const s = shadow === 'sm' ? 'rhd-card--shadow-sm' : shadow === 'md' ? 'rhd-card--shadow' : shadow === 'lg' ? 'rhd-card--shadow-lg' : '';
    return /*#__PURE__*/ _jsx("div", {
        className: cx('rhd-card', s, className),
        ...rest
    });
}
/** Mock browser window for product close-ups. */ export function Window({ url, children, style }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-window",
        style: style,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rhd-window__bar",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "rhd-window__dots",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-window__dot"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-window__dot"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-window__dot"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-window__url",
                        children: url
                    })
                ]
            }),
            children
        ]
    });
}
/**
 * A real screen from the app in a browser frame, with a caption. Pass `darkSrc` (the same
 * screen in dark mode) and each theme shows its own; only the shown one loads. `invert` shows
 * the other theme's screen instead (dark on a light page) in a frame to match, so the app
 * stands off the page around it. Give the image's intrinsic width and height so the page
 * doesn't jump while it loads.
 */ export function Screenshot({ src, darkSrc, alt, width, height, url, caption, eager, invert, bare, className }) {
    const [onLight, onDark] = invert && darkSrc ? [
        darkSrc,
        src
    ] : [
        src,
        darkSrc
    ];
    const img = (s, theme)=>/*#__PURE__*/ _jsx("img", {
            className: cx('rhd-shot__img', darkSrc && `rhd-shot__img--${theme}`),
            src: s,
            alt: alt,
            width: width,
            height: height,
            loading: eager ? 'eager' : 'lazy',
            decoding: "async"
        });
    const imgs = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            img(onLight, 'light'),
            onDark && img(onDark, 'dark')
        ]
    });
    return /*#__PURE__*/ _jsxs("figure", {
        className: cx('rhd-shot', className),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cx(invert && 'rhd-invert', bare && 'rhd-shot__crop'),
                children: bare ? imgs : /*#__PURE__*/ _jsx(Window, {
                    url: url,
                    children: imgs
                })
            }),
            caption && /*#__PURE__*/ _jsx("figcaption", {
                className: "rhd-shot__caption",
                children: caption
            })
        ]
    });
}
/**
 * Who a path is for, its one-line promise and the way in: the home page's "I run a dealership"
 * / "I'm importing my own" split, and the "not you?" card at the foot of each audience page.
 */ export function AudienceCard({ k, who, title, promise, points, action, aside, titleAs: TitleTag = 'h2', className, ...rest }) {
    return /*#__PURE__*/ _jsxs("article", {
        className: cx('rhd-audience', className),
        ...rest,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rhd-audience__head",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-audience__k",
                        "aria-hidden": "true",
                        children: k
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "rhd-audience__who",
                                children: who
                            }),
                            /*#__PURE__*/ _jsx(TitleTag, {
                                className: "rhd-audience__title",
                                children: title
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "rhd-audience__promise",
                children: promise
            }),
            points && points.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "rhd-audience__points",
                children: points.map((p, i)=>/*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsx("span", {
                            children: p
                        })
                    }, i))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rhd-audience__foot",
                children: [
                    action,
                    aside && /*#__PURE__*/ _jsx("span", {
                        className: "rhd-audience__aside",
                        children: aside
                    })
                ]
            })
        ]
    });
}
/* ---------- chips & pills ---------- */ /** Mono filter chip (search criteria, specs). */ export function Chip({ children, ink, style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-chip', ink && 'rhd-chip--ink'),
        style: style,
        children: children
    });
}
/** A recognised paperwork type, or a missing one. */ export function DocChip({ k, missing, children }) {
    if (missing) return /*#__PURE__*/ _jsxs("span", {
        className: "rhd-doc rhd-doc--missing",
        children: [
            "○ ",
            children,
            " — missing"
        ]
    });
    return /*#__PURE__*/ _jsxs("span", {
        className: "rhd-doc",
        children: [
            k && /*#__PURE__*/ _jsx("span", {
                className: "rhd-doc__k",
                children: k
            }),
            children
        ]
    });
}
const STAGE_LABEL = {
    watching: 'Watching',
    bidding: 'Bid',
    won: 'Won',
    importing: 'Importing',
    owned: 'In stock',
    sold: 'Sold'
};
export function StagePill({ stage, children }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-pill', `rhd-pill--${stage}`),
        children: children ?? STAGE_LABEL[stage]
    });
}
/* ---------- costs ---------- */ /** One landed-cost line with its basis and source document. */ export function CostLine({ label, amount, basis, source }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-cost-line",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-cost-line__label",
                children: label
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-cost-line__amount",
                children: amount
            }),
            (basis || source) && /*#__PURE__*/ _jsx("span", {
                className: "rhd-cost-line__basis",
                children: basis
            }),
            (basis || source) && /*#__PURE__*/ _jsx("span", {
                className: "rhd-cost-line__source",
                children: source && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        "↗ ",
                        source
                    ]
                })
            })
        ]
    });
}
/* ---------- steps & timeline ---------- */ /** Kanji-numbered explanation step (壱, 弐, 参…). */ export function AnnotatedStep({ k, title, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-step",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-step__k",
                children: k
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rhd-step__body",
                children: [
                    /*#__PURE__*/ _jsx("b", {
                        className: "rhd-step__title",
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-step__text",
                        children: children
                    })
                ]
            })
        ]
    });
}
/** Red circled callout number (①) used inside annotated close-ups. */ export function Callout({ children }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-callout",
        children: children
    });
}
export function Station({ k, done }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-station', done ? 'rhd-station--done' : 'rhd-station--next'),
        children: k
    });
}
export function StationLink() {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-station-link"
    });
}
/* ---------- FAQ ---------- */ export function FaqItem({ q, children, open, size = 'md' }) {
    return /*#__PURE__*/ _jsxs("details", {
        className: cx('rhd-faq', size === 'lg' && 'rhd-faq--lg'),
        open: open,
        children: [
            /*#__PURE__*/ _jsxs("summary", {
                className: "rhd-faq__q",
                children: [
                    q,
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-plus",
                        children: "+"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "rhd-faq__a",
                children: children
            })
        ]
    });
}
/* ---------- forms ---------- */ export function Field({ label, hint, children }) {
    return /*#__PURE__*/ _jsxs("label", {
        className: "rhd-field",
        children: [
            label,
            hint && /*#__PURE__*/ _jsx("span", {
                className: "rhd-field__hint",
                children: hint
            }),
            children
        ]
    });
}
/** `quiet` = rule-coloured border for optional fields; `mono` for codes and license numbers. */ export function Input({ className, quiet, mono, ...rest }) {
    return /*#__PURE__*/ _jsx("input", {
        className: cx('rhd-input', quiet && 'rhd-input--quiet', mono && 'rhd-input--mono', className),
        ...rest
    });
}
export function Select({ className, ...rest }) {
    return /*#__PURE__*/ _jsx("select", {
        className: cx('rhd-input', className),
        ...rest
    });
}
export function Textarea({ className, ...rest }) {
    return /*#__PURE__*/ _jsx("textarea", {
        className: cx('rhd-input', className),
        ...rest
    });
}
/** Single-choice row of toggle buttons. */ export function ToggleGroup({ options, value, onChange, label }) {
    return /*#__PURE__*/ _jsx("fieldset", {
        "aria-label": label,
        className: "rhd-toggle-group",
        children: options.map((o)=>/*#__PURE__*/ _jsx("button", {
                type: "button",
                className: "rhd-toggle",
                "aria-pressed": o === value,
                onClick: ()=>onChange(o),
                children: o
            }, o))
    });
}
/** Rotated vermilion ring stamp (受付 on a confirmation, 近日 / 先行 on pricing). */ export function Seal({ children, size = 84, fontSize = 26, border = 2.5, style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-seal",
        style: {
            width: size,
            height: size,
            fontSize,
            borderWidth: border,
            ...style
        },
        children: children
    });
}
export * from './app';
